import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { Info, X } from 'lucide-react'
import { cn, formatMoney, formatPercent } from '@/lib/utils'
import { useQuoteBuilderApi } from '../api/client'
import type { Quote, QuoteLine } from '../api/types'
import { formatQuoteDate } from '../ui/format'
import type { ShippingState } from '../shipping/shipping-api'
import { customerHistoryQueryOptions, onlinePricesQueryOptions } from './pricing-api'
import {
  comparableOnlinePrices,
  deliveryRisk,
  insightTones,
  matchingCustomerPrices,
  pricingDelivery,
  onlineAverage,
  sellCheck,
  smartSell,
  smartSellBasisLabels,
} from './pricing-rules'


/**
 * A hover panel beside an editable value (demo `PricingHover`). The value stays editable; the
 * separate info button opens the same panel on touch and keyboard.
 */
function PricingHover({
  title,
  trigger,
  children,
}: {
  title: string
  trigger: (open: () => void, expanded: boolean, id: string) => ReactNode
  children: ReactNode | ((close: () => void) => ReactNode)
}) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const anchor = useRef<HTMLDivElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const [hovering, setHovering] = useState(false)
  const id = useId()
  const show = () => {
    setHovering(true)
    setOpen(true)
  }
  const leave = () => setHovering(false)
  // Leaving the value or the panel closes it shortly after, unless the pointer comes back.
  useEffect(() => {
    if (hovering || !open) return
    const timer = setTimeout(() => setOpen(false), 180)
    return () => clearTimeout(timer)
  }, [hovering, open])
  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const rect = anchor.current?.getBoundingClientRect()
      if (!rect) return
      const width = Math.min(360, window.innerWidth - 24)
      const height = Math.min(panel.current?.offsetHeight ?? 400, window.innerHeight - 24)
      const top = rect.bottom + height + 18 <= window.innerHeight ? rect.bottom + 6 : rect.top - height - 6
      setPosition({ left: Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12)), top: Math.max(12, top) })
    }
    place()
    const observer = new ResizeObserver(place)
    if (panel.current) observer.observe(panel.current)
    const dismiss = (event: PointerEvent) => {
      if (!anchor.current?.contains(event.target as Node) && !panel.current?.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    document.addEventListener('pointerdown', dismiss)
    document.addEventListener('keydown', escape)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
      document.removeEventListener('pointerdown', dismiss)
      document.removeEventListener('keydown', escape)
    }
  }, [open])
  return (
    <div
      onPointerDownCapture={(event) => {
        if ((event.target as Element).closest('[aria-label^="Edit "]')) setOpen(false)
      }}
      onPointerEnter={(event) => {
        if (event.pointerType !== 'touch' && !event.currentTarget.querySelector('input')) show()
      }}
      onPointerLeave={leave}
      ref={anchor}
    >
      {trigger(show, open, id)}
      {open
        ? createPortal(
            <div
              aria-label={title}
              className="fixed z-50 max-h-[calc(100dvh-24px)] w-[calc(100vw-24px)] max-w-[360px] overflow-y-auto rounded-lg border bg-popover p-4 text-left text-xs text-popover-foreground shadow-lg"
              id={id}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) leave()
              }}
              onFocus={show}
              onPointerEnter={show}
              onPointerLeave={leave}
              ref={panel}
              role="region"
              style={position}
            >
              <header className="mb-3 flex items-center justify-between gap-3">
                <h4 className="text-sm font-semibold">{title}</h4>
                <button
                  aria-label={`Close ${title}`}
                  className="grid size-8 shrink-0 place-items-center rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                  onClick={() => setOpen(false)}
                  type="button"
                >
                  <X aria-hidden="true" className="size-4" />
                </button>
              </header>
              {typeof children === 'function' ? children(() => setOpen(false)) : children}
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}

/** Lead time with the delivery buffer against Latest Deliver By (demo `PricingLeadTime`). */
export function PricingLeadTime({ line, quote, shipping }: { line: QuoteLine; quote?: Quote; shipping?: ShippingState }) {
  const delivery = pricingDelivery(line, shipping)
  const risk = deliveryRisk(quote?.latest_delivery_date, delivery?.arrival)
  const label = line.fulfillment?.lead_time?.label || 'Lead time unavailable'
  return (
    <PricingHover
      title="Delivery timing"
      trigger={(toggle, open, id) => (
        <button
          aria-controls={open ? id : undefined}
          aria-expanded={open}
          aria-label={`Delivery timing for ${line.model || line.description}`}
          className={cn(
            'mt-1 flex min-h-8 items-center gap-1 rounded-md border px-1.5 text-left text-[11px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
            insightTones[risk.tone],
          )}
          onClick={toggle}
          onFocus={toggle}
          type="button"
        >
          {label}
          <Info aria-hidden="true" className="size-3 shrink-0" />
        </button>
      )}
    >
      <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
        <dt className="text-muted-foreground">Ship-ready date</dt>
        <dd>{delivery?.ready ? formatQuoteDate(delivery.ready) : 'Unavailable'}</dd>
        <dt className="text-muted-foreground">Latest arrival estimate</dt>
        <dd>{delivery?.arrival ? formatQuoteDate(delivery.arrival) : 'Unavailable'}</dd>
        <dt className="text-muted-foreground">Latest Deliver By</dt>
        <dd>{quote?.latest_delivery_date ? formatQuoteDate(quote.latest_delivery_date) : 'Not set'}</dd>
      </dl>
      <p className={cn('mt-3 rounded-md border p-2 font-medium', insightTones[risk.tone])}>
        {risk.slack === null
          ? 'Set Latest Deliver By and confirm delivery timing to assess the buffer.'
          : risk.slack < 0
            ? `${Math.abs(risk.slack)} day${risk.slack === -1 ? '' : 's'} late. Review faster delivery or an alternative.`
            : risk.slack >= 21
              ? `${risk.slack} days before the deadline: at least a three-week buffer.`
              : `${risk.slack} day${risk.slack === 1 ? '' : 's'} before the deadline: less than a three-week buffer.`}
      </p>
      {delivery ? (
        <p className="mt-2 text-[11px] text-muted-foreground">
          {delivery.method}
          {delivery.planned ? ' planning estimate' : ' estimate'}; uses this manufacturer's ship-ready date and the upper transit range.
        </p>
      ) : null}
    </PricingHover>
  )
}

function SellIntelContent({
  line,
  quote,
  net,
  floor,
  onApplySell,
  close,
  warning,
  warningTone,
}: {
  line: QuoteLine
  quote?: Quote
  net: number
  floor: number
  onApplySell?: (value: string) => Promise<boolean>
  close: () => void
  /** Why the sell field is red or neutral, if it is. */
  warning: string | null
  warningTone: keyof typeof insightTones
}) {
  const api = useQuoteBuilderApi()
  const observations = useQuery({ ...onlinePricesQueryOptions(api, line.product_id ?? 0), enabled: Boolean(line.product_id) })
  const history = useQuery({ ...customerHistoryQueryOptions(api, quote?.customer_id ?? 0), enabled: Boolean(quote?.customer_id) })
  const [applying, setApplying] = useState(false)
  const [applyError, setApplyError] = useState(false)
  const listings = observations.data?.observations ?? []
  const comparable = comparableOnlinePrices(listings)
  const online = onlineAverage(listings)
  const accepted = (history.data ?? []).filter((item) => item.id !== quote?.id && ['approved', 'started'].includes(item.status))
  const previous = quote ? matchingCustomerPrices(line, quote, history.data ?? []) : []
  const previousQuantity = previous.reduce((total, item) => total + Number(item.line.quantity), 0)
  const previousSell = previousQuantity
    ? previous.reduce((total, item) => total + Number(item.line.sell_amount ?? item.line.list_amount) * Number(item.line.quantity), 0) /
      previousQuantity
    : null
  const amountOrNull = (value: string | null | undefined) => (value == null ? null : Number(value))
  const smart = smartSell({
    net,
    floor,
    map: amountOrNull(line.map_amount),
    online: amountOrNull(observations.data?.lowest_online_amount),
    store: amountOrNull(observations.data?.store_amount),
  })
  const suggestion = smart.amount
  const lowestOnline = amountOrNull(observations.data?.lowest_online_amount)
  const sell = Number(line.sell_amount ?? line.list_amount)
  const suggestedMargin = suggestion > 0 ? ((suggestion - net) / suggestion) * 100 : 0
  const loading = observations.isFetching && !observations.data
  const apply = async () => {
    if (!onApplySell || applying) return
    setApplying(true)
    setApplyError(false)
    const saved = await onApplySell(suggestion.toFixed(2))
    setApplying(false)
    if (saved) close()
    else setApplyError(true)
  }
  return (
    <>
      <p className="mb-3 text-muted-foreground">
        {line.manufacturer} {line.model}
      </p>
      {warning ? (
        <p className={cn('mb-3 rounded-md border p-2 font-medium', insightTones[warningTone])} role="status">
          {warning}
        </p>
      ) : null}
      <dl className="grid grid-cols-2 gap-x-3 gap-y-2 tabular-nums">
        <dt className="text-muted-foreground">Your Sell</dt>
        <dd className="text-right font-semibold">{formatMoney(sell)}</dd>
        <dt className="text-muted-foreground">Lowest online</dt>
        <dd className="text-right font-semibold">
          {loading ? 'Loading...' : lowestOnline === null ? 'No online prices' : formatMoney(lowestOnline)}
        </dd>
        <dt className="text-muted-foreground">Store price</dt>
        <dd className="text-right font-semibold">
          {loading ? 'Loading...' : observations.data?.store_amount == null ? '-' : formatMoney(Number(observations.data.store_amount))}
        </dd>
        <dt className="text-muted-foreground">Online average</dt>
        <dd className="text-right font-semibold">
          {loading ? 'Loading...' : online === null ? '-' : formatMoney(online)}
        </dd>
        <dt className="text-muted-foreground">MAP</dt>
        <dd className="text-right font-semibold">{line.map_amount == null ? '-' : formatMoney(Number(line.map_amount))}</dd>
      </dl>
      {listings.length ? (
        <details className="mt-3 border-t pt-2">
          <summary className="cursor-pointer text-[11px] font-semibold">
            {listings.length} website listing{listings.length === 1 ? '' : 's'}
            {comparable.length !== listings.length ? `, ${comparable.length} comparable` : ''}
          </summary>
          {listings.map((item) => (
            <div className="mt-2 text-[11px]" key={`${item.retailer}-${item.observed_on}`}>
              {/* eslint-disable-next-line no-restricted-syntax -- external retailer listing */}
              <a className="font-semibold text-primary underline" href={item.url} rel="noreferrer" target="_blank">
                {item.retailer}
              </a>{' '}
              · {formatMoney(Number(item.amount))} · {formatQuoteDate(item.observed_on)}
              <p className="mt-1 text-muted-foreground">
                {item.unit} · {item.evidence}
              </p>
            </div>
          ))}
        </details>
      ) : null}
      {net > 0 ? (
        <section className={cn('mt-3 rounded-md border p-2.5', insightTones.green)}>
          <h5 className="font-semibold">Smart price</h5>
          <button
            aria-label={`Set sell to ${formatMoney(suggestion)}`}
            className="mt-1.5 flex w-full items-center justify-between gap-3 rounded-md border border-emerald-300 bg-background px-3 py-2 text-left hover:border-emerald-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 disabled:cursor-wait disabled:opacity-60"
            disabled={!onApplySell || applying}
            onClick={() => void apply()}
            type="button"
          >
            <span className="text-base font-bold tabular-nums text-emerald-800 dark:text-emerald-300">{formatMoney(suggestion)}</span>
            <span className="text-[11px] font-semibold">
              {applying ? 'Applying...' : Math.round(suggestion * 100) === Math.round(sell * 100) ? 'Current price' : 'Use this price'}
            </span>
          </button>
          <p className="mt-1.5 text-[11px]">
            {loading ? 'Checking online prices...' : smartSellBasisLabels[smart.basis]} · {formatPercent(suggestedMargin)} Margin
          </p>
          {applyError ? (
            <p className="mt-1 text-[11px] text-destructive" role="alert">
              The price was not saved. Try again.
            </p>
          ) : null}
        </section>
      ) : (
        <p className="mt-3 rounded-md border p-2 text-muted-foreground">Confirm Net before setting a target sell price.</p>
      )}
      <section className="mt-3 border-t pt-3">
        <h5 className="font-semibold">{quote?.customer?.company_name || 'Customer history'}</h5>
        {history.isFetching ? (
          <p className="mt-1 text-muted-foreground">Loading history...</p>
        ) : history.isError ? (
          <button className="mt-1 text-primary underline" onClick={() => void history.refetch()} type="button">
            Retry customer history
          </button>
        ) : (
          <>
            <p className="mt-1 text-muted-foreground">
              {accepted.length} accepted quote{accepted.length === 1 ? '' : 's'} ·{' '}
              {formatMoney(accepted.reduce((sum, item) => sum + Number(item.totals.sell), 0))} quoted value
            </p>
            <p className="mt-1">
              {previousSell === null
                ? 'No accepted price for this model and configuration.'
                : `${formatMoney(previousSell)} average unit sell across ${previousQuantity} units of this configuration.`}
            </p>
          </>
        )}
      </section>
    </>
  )
}

/** The sell field, toned by Margin, with the price intel panel (demo `PricingSell`). */
export function PricingSell({
  line,
  quote,
  net,
  floor,
  minimum,
  onApplySell,
  children,
}: {
  line: QuoteLine
  quote?: Quote
  net: number
  floor: number
  /** The line's minimum sell (minimum Margin or UMRP), when known. */
  minimum: number | null
  onApplySell?: (value: string) => Promise<boolean>
  children: ReactNode
}) {
  const api = useQuoteBuilderApi()
  const prices = useQuery({ ...onlinePricesQueryOptions(api, line.product_id ?? 0), enabled: Boolean(line.product_id) })
  const amountOrNull = (value: string | null | undefined) => (value == null ? null : Number(value))
  const check = sellCheck({
    sell: Number(line.sell_amount ?? line.list_amount),
    net,
    minimum,
    map: amountOrNull(line.map_amount),
    online: amountOrNull(prices.data?.lowest_online_amount),
    store: amountOrNull(prices.data?.store_amount),
  })
  const tone = check.tone
  return (
    <PricingHover
      title="Price intel"
      trigger={(toggle, open, id) => (
        <div className={cn('relative rounded-md border py-0.5 pl-2 pr-8', insightTones[tone])}>
          {children}
          <button
            aria-controls={open ? id : undefined}
            aria-expanded={open}
            aria-label={`Price intel for ${line.model || line.description}`}
            className="absolute right-0.5 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            onClick={toggle}
            onFocus={toggle}
            type="button"
          >
            <Info aria-hidden="true" className="size-3" />
          </button>
        </div>
      )}
    >
      {(close) => (
        <SellIntelContent
          close={close}
          floor={floor}
          line={line}
          net={net}
          onApplySell={onApplySell}
          quote={quote}
          warning={check.reason}
          warningTone={tone}
        />
      )}
    </PricingHover>
  )
}
